import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { ArrowRight, Sparkles, Clock, Shield, Zap, MapPin } from "lucide-react";
import { TIME_SLOTS, DAY_START_HOUR } from "../lib/constants";
import { useSettings } from "../lib/SettingsContext";
import { useCatalog } from "../lib/CatalogContext";

function getNextSlot() {
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes() + 30; // 30-min buffer
  const slot = TIME_SLOTS.find((s) => {
    const [h, m] = s.split(":").map(Number);
    return h * 60 + m >= nowMinutes;
  });
  if (slot) return `TODAY · ${slot}`;
  return `TOMORROW · ${String(DAY_START_HOUR).padStart(2, "0")}:00`;
}

export default function Landing() {
  const nextSlot = getNextSlot();
  const { bookingEnabled } = useSettings();
  const { packages } = useCatalog();
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      <Header />

      {/* HERO */}
      <section className="pt-32 pb-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-8 relative">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="font-mono-accent text-xs text-[#002FA7] mb-6">
              [ INDIA · EST. 2026 · NO. 001 ]
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-8">
              THE EXPRESS<br />
              CAR WASH.<br />
              <span className="text-[#002FA7]">ON DEMAND.</span><br />
              <span className="text-[#002FA7]">ZERO WAIT. *</span>
            </h1>
            <p className="text-lg text-[#525252] max-w-xl mb-10 leading-relaxed">
              Slot-based booking. Subscription plans. Professional crews.
              Built for the cars that move modern India.
            </p>
            <div className="flex flex-wrap gap-4">
              {bookingEnabled ? (
                <Link to="/book" data-testid="hero-book-cta">
                  <Button className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wider px-8 py-6 text-sm h-auto">
                    Book a Wash <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              ) : (
                <Button
                  data-testid="hero-book-cta"
                  disabled
                  className="bg-[#D4D4D8] text-[#525252] rounded-none font-bold uppercase tracking-wider px-8 py-6 text-sm h-auto cursor-not-allowed"
                >
                  Coming Soon
                </Button>
              )}
              <Link to="/subscriptions" data-testid="hero-subscribe-cta">
                <Button variant="outline" className="border-2 border-black bg-transparent hover:bg-black hover:text-white rounded-none font-bold uppercase tracking-wider px-8 py-6 text-sm h-auto">
                  See Plans
                </Button>
              </Link>
              <a href="https://maps.app.goo.gl/T9syy6Qj8Jyna8QBA?g_st=iw" target="_blank" rel="noopener noreferrer" data-testid="hero-store-locator-cta">
                <Button variant="outline" className="border-2 border-black bg-transparent hover:bg-black hover:text-white rounded-none font-bold uppercase tracking-wider px-8 py-6 text-sm h-auto">
                  <MapPin size={16} className="mr-2" /> Store Locator
                </Button>
              </a>
            </div>

          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="relative border border-[#D4D4D8]">
              <img
                src="https://images.unsplash.com/photo-1605164598708-25701594473e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxjYXIlMjB3YXNoJTIwd2F0ZXIlMjBzcGxhc2h8ZW58MHx8fHwxNzgxOTM2MTA3fDA&ixlib=rb-4.1.0&q=85"
                alt="Car wash"
                className="w-full aspect-[4/5] object-cover grayscale-[20%]"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-[#D4D4D8] p-4 flex items-center justify-between">
                <div>
                  <div className="font-mono-accent text-[10px] text-[#525252]">NEXT AVAILABLE</div>
                  <div className="font-display font-bold text-lg">{nextSlot}</div>
                </div>
                {bookingEnabled ? (
                  <Link to="/book" className="font-mono-accent text-xs text-[#002FA7] hover:underline">→ BOOK</Link>
                ) : (
                  <span className="font-mono-accent text-xs text-[#D4D4D8]">COMING SOON</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="border-y border-[#D4D4D8] py-4 overflow-hidden bg-[#0A0A0A] text-white">
        <div className="marquee-track flex gap-12 whitespace-nowrap font-mono-accent text-sm">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-12 shrink-0">
              <span>● FOAM WASH</span><span>● INTERIOR DETAIL</span>
              <span>● TYRE SHINE</span>
              <span>● MONTHLY PLANS</span><span>● PRO CREW</span>
            </div>
          ))}
        </div>
      </div>

      {/* WHY */}
      <section className="py-24 px-6 bg-[#F4F4F5] border-y border-[#D4D4D8]">
        <div className="max-w-7xl mx-auto">
          <div className="font-mono-accent text-xs text-[#002FA7] mb-3">WHY BAYWASH</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-12 max-w-2xl">
            A CAR WASH EXPERIENCE LIKE NEVER BEFORE.
          </h2>
          <div className="grid md:grid-cols-4 gap-px bg-[#D4D4D8]">
            {[
              { icon: Clock, t: "Slot-based", d: "Pick your time. We show up. Zero waiting." },
              { icon: Shield, t: "Pro Crews", d: "Trained detailers. Safe products. No surprises." },
              { icon: Zap, t: "Subscriptions", d: "Monthly plans from ₹1499. Predictable pricing." },
              { icon: Sparkles, t: "Premium Finish", d: "Showroom-grade finish on every visit." },
            ].map((f, i) => (
              <div key={i} className="bg-white p-8">
                <f.icon size={28} strokeWidth={1.5} className="text-[#002FA7] mb-6" />
                <h3 className="font-display font-bold text-xl mb-2">{f.t}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUBSCRIPTION TEASER */}
      <section className="py-24 px-6 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono-accent text-xs text-[#FFC000] mb-3">PLANS</div>
            <h2 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
              Subscribe.<br />
              Drive clean.<br />
              <span className="text-[#FFC000]">Forever.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <div className="space-y-4 mb-8">
              {packages.map((p) => (
                <div key={p.id} className="flex items-center justify-between border-b border-white/20 pb-4">
                  <div>
                    <div className="font-display font-bold text-xl text-white">{p.code}: {p.name}</div>
                    <div className="text-sm text-white/60">{p.washes} · {p.durationMonths} mo</div>
                  </div>
                  <div className="font-display font-black text-2xl text-white">₹{p.priceHatchback.toLocaleString("en-IN")}</div>
                </div>
              ))}
            </div>
            <Link to="/subscriptions" data-testid="landing-subscribe-cta">
              <Button className="bg-[#FFC000] text-black hover:bg-white rounded-none font-bold uppercase tracking-wider px-8 py-6 text-sm h-auto w-full sm:w-auto">
                Choose Your Plan <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
